/**
 * useReorderableList — focus retention + screen-reader announcements for any
 * list the operator reorders in place.
 *
 * Extracted because this exact pattern was hand-rolled three times
 * (PicklistOrderSection twice, RankedSupplierCostList once), two of them
 * leaking their ref map, and a fourth site (RedoReasonsSection) skipped it
 * entirely and still carries the bug it exists to prevent:
 *
 *   Removing a row, or disabling a chevron on activation, unmounts the focused
 *   button and drops focus to <body> mid-reorder. Keyboard users lose their
 *   place in the list every time they use it.
 *
 * Contract:
 *   - `registerRef(key)` on every row control that could be the focus target.
 *     It PRUNES on unmount, so the map cannot grow across reorders.
 *   - `addRef` on the "Add …" trigger — the fallback when a list empties.
 *   - `focusAfter(key | null)` after a mutation; null targets the add trigger.
 *   - `announce(text)` for chevron moves, adds, removes, and no-ops.
 *   - Render `liveText` in ONE `aria-live="polite"` region per list.
 *
 * Pass the list itself (or its length) as `deps` so the focus effect re-runs
 * after the mutation has rendered.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/** Focus sentinel: target the "Add …" trigger when no sibling row remains. */
export const REORDER_ADD_FOCUS = '__add__';

export function useReorderableList<T extends HTMLElement = HTMLButtonElement>(deps: unknown) {
  const refs = useRef<Map<string, T | null>>(new Map());
  const addRef = useRef<T | null>(null);
  const [pendingFocus, setPendingFocus] = useState<string | null>(null);
  // A screen reader skips an identical string in the same node, so a repeated
  // move ("moved to position 2" twice) would be silent. Alternating an
  // invisible NBSP makes every announcement a real DOM change.
  const [live, setLive] = useState({ text: '', n: 0 });

  const registerRef = useCallback(
    (key: string) => (el: T | null) => {
      if (el) refs.current.set(key, el);
      else refs.current.delete(key);
    },
    [],
  );

  const focusAfter = useCallback((key: string | null) => {
    setPendingFocus(key ?? REORDER_ADD_FOCUS);
  }, []);

  const announce = useCallback((text: string) => {
    setLive((prev) => ({ text, n: prev.n + 1 }));
  }, []);

  useEffect(() => {
    if (pendingFocus === null) return;
    const el = pendingFocus === REORDER_ADD_FOCUS ? addRef.current : refs.current.get(pendingFocus);
    if (el) {
      el.focus();
      setPendingFocus(null);
    }
    // `deps` is the caller's list — the effect must re-run once the mutation
    // has rendered, or the target ref does not exist yet.
  }, [pendingFocus, deps]);

  return {
    registerRef,
    addRef,
    focusAfter,
    announce,
    // Trailing non-breaking space on alternate announcements: a screen
    // reader skips an identical string in the same node, so a repeated move
    // ("moved to position 2" twice) would otherwise be silent.
    liveText: live.n % 2 === 0 ? live.text : `${live.text}\u00a0`,
  };
}
